import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  createInvoice,
  getInvoices,
  deleteInvoice,
  updateInvoice,
  getInvoiceById,
} from 'data';


interface NewInvoice {
	clientId: number;
	clientName: string;
	status: string;
	startDate: string;
	endDate: string;
	assignmentRole: string;
	grossAmount: number;
	currency: string;
	hours: number;
	days: number;
	timeRate: number;
	paymentTerms: string;
	shippingDate: string;
	invoiceText: string;
	note: string;
	amountInclVAT: number;
	invoiceType: string;
	contactEmail?: string;
}

interface Invoice extends NewInvoice {
  id: number;
  invoiceNumber: string;
  invoiceDocId?: number;
  payslipDocId?: number;
  payslipAmount: string;
  payslipNumber: string;
  placementNumber: string;
  invoiceSentDate: string;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceState {
  invoices: Invoice[];
  invoice: Invoice | null;
  count: number;
  page: number;
  per_page: number;
  getInvoicesLoading: boolean;
  addInvoiceLoading: boolean;
  updateInvoiceLoading: boolean;
  deleteInvoiceLoading: boolean;
  getInvoiceLoading: boolean;
  error: string | null;
}

const initialState: InvoiceState = {
  invoices: [],
  invoice: null,
  count: 0,
  page: 1,
  per_page: 10,
  getInvoicesLoading: false,
  addInvoiceLoading: false,
  updateInvoiceLoading: false,
  deleteInvoiceLoading: false,
  getInvoiceLoading: false,
  error: null,
};

// ✅ Thunk to fetch invoices
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (
    { page = 1, per_page = 10 }: { page?: number; per_page?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await getInvoices(page, per_page);
      return {
        invoices: response.data.data,
        count: response.data.count,
        page,
        per_page,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch invoices',
      );
    }
  },
);

export const addInvoice = createAsyncThunk(
  'invoices/addInvoice',
  async (
    invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>,
    { rejectWithValue },
  ) => {
    try {
      const sanitizedInvoice: NewInvoice = {
        ...invoiceData,
        status: invoiceData.status ?? 'pending',
        startDate: invoiceData.startDate,
        endDate: invoiceData.endDate,
      };

      const response = await createInvoice(sanitizedInvoice);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to create invoice',
      );
    }
  },
);

export const updateInvoiceById = createAsyncThunk(
  'invoices/updateInvoice',
  async (invoiceData: Partial<Invoice>, { rejectWithValue }) => {
    try {
      const response = await updateInvoice(invoiceData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to update invoice',
      );
    }
  },
);

export const deleteInvoiceById = createAsyncThunk(
  'invoices/deleteInvoice',
  async (invoiceId: number, { rejectWithValue }) => {
    try {
      await deleteInvoice(invoiceId);
      return invoiceId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to delete invoice',
      );
    }
  },
);

export const fetchInvoiceById = createAsyncThunk(
  'invoices/fetchInvoiceById',
  async (invoiceId: number, { rejectWithValue }) => {
    try {
      const response = await getInvoiceById(invoiceId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoice');
    }
  },
);

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.getInvoicesLoading = true;
      })
      .addCase(
        fetchInvoices.fulfilled,
        (
          state,
          action: PayloadAction<{
            invoices: Invoice[];
            count: number;
            page: number;
            per_page: number;
          }>,
        ) => {
          state.getInvoicesLoading = false;
          state.invoices = action.payload.invoices;
          state.count = action.payload.count;
          state.page = action.payload.page;
          state.per_page = action.payload.per_page;
          state.error = null;
        },
      )
      .addCase(
        fetchInvoices.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.getInvoicesLoading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch invoices';
        },
      )
      .addCase(addInvoice.pending, (state) => {
        state.addInvoiceLoading = true;
      })
      .addCase(
        addInvoice.fulfilled,
        (state, action: PayloadAction<Invoice>) => {
          state.addInvoiceLoading = false;
          state.invoices.push(action.payload);
          state.error = null;
        },
      )
      .addCase(
        addInvoice.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.addInvoiceLoading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to create invoice';
        },
      )
      .addCase(updateInvoiceById.pending, (state) => {
        state.updateInvoiceLoading = true;
      })
      .addCase(updateInvoiceById.fulfilled, (state, action) => {
        state.updateInvoiceLoading = false;
        const updatedInvoice = action.payload;
        const index = state.invoices.findIndex(
          (invoice) => invoice.id === updatedInvoice.id,
        );
        if (index !== -1) {
          state.invoices[index] = updatedInvoice;
        }
      })
      .addCase(
        updateInvoiceById.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.updateInvoiceLoading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update invoice';
        },
      )
      .addCase(deleteInvoiceById.pending, (state) => {
        state.deleteInvoiceLoading = true;
      })
      .addCase(
        deleteInvoiceById.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.deleteInvoiceLoading = false;
          state.invoices = state.invoices.filter(
            (invoice) => invoice.id !== action.payload,
          );
          state.error = null;
        },
      )
      .addCase(
        deleteInvoiceById.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.deleteInvoiceLoading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to delete invoice';
        },
      )
      .addCase(fetchInvoiceById.pending, (state) => {
        state.getInvoiceLoading = true;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.getInvoiceLoading = false;
        state.invoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.getInvoiceLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const invoiceReducer = invoiceSlice.reducer;
